import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { can } from '@/lib/permissions'
import {
  Loader2, AlertCircle, Clock, CheckCircle, XCircle, MessageSquare,
  Users, TrendingUp, BarChart3, Filter, X, UserCheck
} from 'lucide-react'
import { ticketsAPI, authAPI, helpdeskGroupsAPI } from '@/lib/api'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { io } from 'socket.io-client'

const CATEGORIES = {
  network: 'Netwerk',
  hardware: 'Hardware',
  gaming: 'Gaming',
  software: 'Software',
  other: 'Andere'
}

const PRIORITIES = {
  low: { label: 'Laag', color: 'bg-blue-500' },
  normal: { label: 'Normaal', color: 'bg-green-500' },
  high: { label: 'Hoog', color: 'bg-orange-500' },
  urgent: { label: 'Urgent', color: 'bg-red-500' }
}

const STATUS_CONFIG = {
  open: { label: 'Open', icon: AlertCircle, color: 'text-blue-500', bgColor: 'bg-blue-500' },
  in_progress: { label: 'Bezig', icon: Clock, color: 'text-yellow-500', bgColor: 'bg-yellow-500' },
  waiting_user: { label: 'Wacht op gebruiker', icon: MessageSquare, color: 'text-purple-500', bgColor: 'bg-purple-500' },
  resolved: { label: 'Opgelost', icon: CheckCircle, color: 'text-green-500', bgColor: 'bg-green-500' },
  closed: { label: 'Gesloten', icon: XCircle, color: 'text-gray-500', bgColor: 'bg-gray-500' }
}

export default function HelpdeskPanelPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [tickets, setTickets] = useState([])
  const [stats, setStats] = useState(null)
  const [helpdeskUsers, setHelpdeskUsers] = useState([])
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [statsLoading, setStatsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  // Filters
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    priority: 'all',
    assigned_to: 'all',
    assigned_group_id: 'all'
  })

  // Assignment dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [selectedTicket, setSelectedTicket] = useState(null)
  const [assigning, setAssigning] = useState(false)

  useEffect(() => {
    loadTickets()
    loadStats()
    loadHelpdeskUsers()
    loadGroups()
  }, [filters])

  // Socket.IO for real-time updates
  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    if (!token) {
      console.log('No token found, skipping socket connection')
      return
    }

    console.log('Setting up socket connection...')
    const socket = io({
      auth: { token },
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    })

    socket.on('connect', () => {
      console.log('✅ Connected to helpdesk socket, ID:', socket.id)
      socket.emit('join-helpdesk')
      console.log('📡 Emitted join-helpdesk event')
    })

    socket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error)
    })

    socket.on('ticket-created', ({ ticket }) => {
      console.log('🎫 New ticket created event received:', ticket)
      loadTickets()
      loadStats()
    })

    socket.on('ticket-updated', ({ ticket }) => {
      console.log('🔄 Ticket updated event received:', ticket)
      loadTickets()
      loadStats()
    })

    socket.on('ticket-assigned', ({ ticket }) => {
      console.log('👤 Ticket assigned event received:', ticket)
      loadTickets()
      loadStats()
    })

    socket.on('ticket-reply-added', ({ ticketId, reply }) => {
      console.log('💬 New reply added to ticket:', ticketId)
      loadTickets()
    })

    socket.on('disconnect', () => {
      console.log('❌ Disconnected from helpdesk socket')
    })

    return () => {
      console.log('🔌 Cleaning up socket connection')
      socket.emit('leave-helpdesk')
      socket.disconnect()
    }
  }, [])

  const loadTickets = async () => {
    try {
      setLoading(true)
      const params = {}
      if (filters.status && filters.status !== 'all') params.status = filters.status
      if (filters.category && filters.category !== 'all') params.category = filters.category
      if (filters.priority && filters.priority !== 'all') params.priority = filters.priority
      if (filters.assigned_to && filters.assigned_to !== 'all') {
        if (filters.assigned_to === 'unassigned') {
          // Backend should handle this
        } else {
          params.assigned_to = filters.assigned_to
        }
      }
      if (filters.assigned_group_id && filters.assigned_group_id !== 'all') {
        params.assigned_group_id = filters.assigned_group_id
      }

      const response = await ticketsAPI.getTickets(params)
      setTickets(response.data.tickets || [])
    } catch (err) {
      console.error('Failed to load tickets:', err)
      setError('Kon tickets niet laden')
    } finally {
      setLoading(false)
    }
  }

  const loadGroups = async () => {
    try {
      const response = await helpdeskGroupsAPI.getGroups()
      setGroups(response.data.groups || [])
    } catch (err) {
      console.error('Failed to load groups:', err)
    }
  }

  const loadStats = async () => {
    try {
      setStatsLoading(true)
      const response = await ticketsAPI.getStats()
      setStats(response.data)
    } catch (err) {
      console.error('Failed to load stats:', err)
    } finally {
      setStatsLoading(false)
    }
  }

  const loadHelpdeskUsers = async () => {
    try {
      const response = await authAPI.searchUsers('') // Get all users
      // Filter to only include helpdesk and admin users
      const filtered = response.data.users.filter(
        u => can(u.role, 'helpdesk_access')
      )
      setHelpdeskUsers(filtered)
    } catch (err) {
      console.error('Failed to load helpdesk users:', err)
    }
  }

  const handleAssignTicket = async (assignedTo) => {
    if (!selectedTicket) return

    setAssigning(true)
    setError(null)

    try {
      await ticketsAPI.assignTicket(selectedTicket.id, assignedTo === 'unassign' ? null : assignedTo)
      setSuccess('Gebruiker toegewezen')
      setAssignDialogOpen(false)
      setSelectedTicket(null)
      loadTickets()
      loadStats()
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to assign ticket:', err)
      setError(err.response?.data?.error || 'Kon ticket niet toewijzen')
    } finally {
      setAssigning(false)
    }
  }

  const handleAssignGroup = async (groupId) => {
    if (!selectedTicket) return

    setAssigning(true)
    setError(null)

    try {
      const finalGroupId = groupId === 'none' ? null : parseInt(groupId)
      await ticketsAPI.assignTicketToGroup(selectedTicket.id, finalGroupId)
      setSuccess('Groep toegewezen')
      setAssignDialogOpen(false)
      setSelectedTicket(null)
      loadTickets()
      loadStats()
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to assign group:', err)
      setError(err.response?.data?.error || 'Kon groep niet toewijzen')
    } finally {
      setAssigning(false)
    }
  }

  const clearFilters = () => {
    setFilters({
      status: 'all',
      category: 'all',
      priority: 'all',
      assigned_to: 'all',
      assigned_group_id: 'all'
    })
  }

  const hasActiveFilters =
    (filters.status && filters.status !== 'all') ||
    (filters.category && filters.category !== 'all') ||
    (filters.priority && filters.priority !== 'all') ||
    (filters.assigned_to && filters.assigned_to !== 'all') ||
    (filters.assigned_group_id && filters.assigned_group_id !== 'all')

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getPriorityColor = (priority) => {
    return PRIORITIES[priority]?.color || 'bg-gray-500'
  }

  const getUserDisplay = (u) => {
    return u.display_name || u.username
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-neon-green mb-2">Helpdesk Panel</h1>
        <p className="text-muted-foreground">Support team ticket management</p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <Alert className="mb-6 border-neon-green bg-neon-green/10">
          <CheckCircle className="h-4 w-4 text-neon-green" />
          <AlertDescription className="text-neon-green">{success}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Statistics */}
      {statsLoading ? (
        <div className="flex items-center justify-center h-32 mb-8">
          <Loader2 className="h-6 w-6 animate-spin text-neon-green" />
        </div>
      ) : stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Card className="border-blue-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Open</p>
                  <p className="text-3xl font-bold text-blue-500">{stats.open || 0}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-yellow-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Bezig</p>
                  <p className="text-3xl font-bold text-yellow-500">{stats.in_progress || 0}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-purple-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Wacht</p>
                  <p className="text-3xl font-bold text-purple-500">{stats.waiting_user || 0}</p>
                </div>
                <MessageSquare className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-green-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Opgelost</p>
                  <p className="text-3xl font-bold text-green-500">{stats.resolved || 0}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-neon-purple/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Totaal</p>
                  <p className="text-3xl font-bold text-neon-green">{stats.total || 0}</p>
                </div>
                <BarChart3 className="h-8 w-8 text-neon-green" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="border-neon-purple/30 mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Filters
              </CardTitle>
              <CardDescription>Filter tickets op status, categorie, prioriteit en toewijzing</CardDescription>
            </div>
            {hasActiveFilters && (
              <Button variant="outline" size="sm" onClick={clearFilters}>
                <X className="h-4 w-4 mr-2" />
                Reset Filters
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <Label>Status</Label>
              <Select value={filters.status} onValueChange={(value) => setFilters({ ...filters, status: value })}>
                <SelectTrigger className="bg-background border-neon">
                  <SelectValue placeholder="Alle statussen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle statussen</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">Bezig</SelectItem>
                  <SelectItem value="waiting_user">Wacht op gebruiker</SelectItem>
                  <SelectItem value="resolved">Opgelost</SelectItem>
                  <SelectItem value="closed">Gesloten</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Categorie</Label>
              <Select value={filters.category} onValueChange={(value) => setFilters({ ...filters, category: value })}>
                <SelectTrigger className="bg-background border-neon">
                  <SelectValue placeholder="Alle categorieën" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle categorieën</SelectItem>
                  <SelectItem value="network">Netwerk</SelectItem>
                  <SelectItem value="hardware">Hardware</SelectItem>
                  <SelectItem value="gaming">Gaming</SelectItem>
                  <SelectItem value="software">Software</SelectItem>
                  <SelectItem value="other">Andere</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Prioriteit</Label>
              <Select value={filters.priority} onValueChange={(value) => setFilters({ ...filters, priority: value })}>
                <SelectTrigger className="bg-background border-neon">
                  <SelectValue placeholder="Alle prioriteiten" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle prioriteiten</SelectItem>
                  <SelectItem value="low">Laag</SelectItem>
                  <SelectItem value="normal">Normaal</SelectItem>
                  <SelectItem value="high">Hoog</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Toegewezen aan</Label>
              <Select value={filters.assigned_to} onValueChange={(value) => setFilters({ ...filters, assigned_to: value })}>
                <SelectTrigger className="bg-background border-neon">
                  <SelectValue placeholder="Iedereen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Iedereen</SelectItem>
                  <SelectItem value="unassigned">Niet toegewezen</SelectItem>
                  {user && (
                    <SelectItem value={user.id.toString()}>
                      Aan mij ({getUserDisplay(user)})
                    </SelectItem>
                  )}
                  {helpdeskUsers.map(u => (
                    <SelectItem key={u.id} value={u.id.toString()}>
                      {getUserDisplay(u)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Groep</Label>
              <Select value={filters.assigned_group_id} onValueChange={(value) => setFilters({ ...filters, assigned_group_id: value })}>
                <SelectTrigger className="bg-background border-neon">
                  <SelectValue placeholder="Alle groepen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle groepen</SelectItem>
                  {groups.map((group) => (
                    <SelectItem key={group.id} value={group.id.toString()}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tickets List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-neon-green" />
        </div>
      ) : tickets.length === 0 ? (
        <Card className="border-neon-purple/30">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <MessageSquare className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">Geen tickets gevonden</h3>
            <p className="text-muted-foreground text-center">
              {hasActiveFilters ? 'Probeer andere filters' : 'Er zijn nog geen tickets'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {tickets.map(ticket => {
            const StatusIcon = STATUS_CONFIG[ticket.status]?.icon || AlertCircle
            const statusColor = STATUS_CONFIG[ticket.status]?.color || 'text-gray-500'
            const isAssignedToMe = user && ticket.assigned_to === user.id

            return (
              <Card
                key={ticket.id}
                className={`border-neon-purple/30 hover:border-neon-purple/60 transition-colors ${
                  isAssignedToMe ? 'border-neon-green/50 bg-neon-green/5' : ''
                }`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div
                      className="flex-1 cursor-pointer"
                      onClick={() => navigate(`/helpdesk/${ticket.id}`)}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="text-xs">
                          #{ticket.id}
                        </Badge>
                        <div className={`h-2 w-2 rounded-full ${getPriorityColor(ticket.priority)}`} />
                        <span className="text-xs text-muted-foreground">
                          {PRIORITIES[ticket.priority]?.label}
                        </span>
                        <span className="text-xs text-muted-foreground">•</span>
                        <span className="text-xs text-muted-foreground">
                          {CATEGORIES[ticket.category]}
                        </span>
                        <span className="text-xs text-muted-foreground">•</span>
                        <span className="text-xs text-muted-foreground">
                          {ticket.creator_display_name || ticket.creator_username}
                        </span>
                        {isAssignedToMe && (
                          <>
                            <span className="text-xs text-muted-foreground">•</span>
                            <Badge variant="default" className="text-xs bg-neon-green text-background">
                              Aan jou
                            </Badge>
                          </>
                        )}
                      </div>
                      <CardTitle className="text-xl mb-1">{ticket.title}</CardTitle>
                      <CardDescription className="line-clamp-2">{ticket.description}</CardDescription>
                    </div>

                    <div className="flex flex-col items-end gap-2 ml-4">
                      <div className={`flex items-center gap-2 ${statusColor}`}>
                        <StatusIcon className="h-4 w-4" />
                        <span className="text-sm font-medium">{STATUS_CONFIG[ticket.status]?.label}</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedTicket(ticket)
                          setAssignDialogOpen(true)
                        }}
                        className="text-xs"
                      >
                        <UserCheck className="h-3 w-3 mr-1" />
                        {ticket.assignee_display_name || ticket.assignee_username || 'Toewijzen'}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <div className="flex items-center gap-4">
                      <span>Aangemaakt: {formatDate(ticket.created_at)}</span>
                      {ticket.location && <span>📍 {ticket.location}</span>}
                      {ticket.unread_count > 0 && (
                        <Badge className="text-xs bg-red-500 hover:bg-red-600 text-white">
                          <MessageSquare className="h-3 w-3 mr-1" />
                          {ticket.unread_count} nieuw
                        </Badge>
                      )}
                      {ticket.reply_count > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          <MessageSquare className="h-3 w-3 mr-1" />
                          {ticket.reply_count} totaal
                        </Badge>
                      )}
                      {ticket.group_name && (
                        <Badge
                          variant="outline"
                          className="text-xs"
                          style={{
                            borderColor: ticket.group_color,
                            color: ticket.group_color
                          }}
                        >
                          <div
                            className="w-2 h-2 rounded-full mr-1"
                            style={{ backgroundColor: ticket.group_color }}
                          />
                          {ticket.group_name}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="bg-card border-neon">
          <DialogHeader>
            <DialogTitle className="text-neon-green">Ticket Toewijzen</DialogTitle>
          </DialogHeader>

          {selectedTicket && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">Ticket #{selectedTicket.id}</p>
                <p className="font-semibold">{selectedTicket.title}</p>
              </div>

              <div>
                <Label>Toewijzen aan gebruiker</Label>
                <Select
                  defaultValue={selectedTicket.assigned_to?.toString() || 'unassign'}
                  onValueChange={handleAssignTicket}
                  disabled={assigning}
                >
                  <SelectTrigger className="bg-background border-neon">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassign">Niet toegewezen</SelectItem>
                    {helpdeskUsers.map(u => (
                      <SelectItem key={u.id} value={u.id.toString()}>
                        {getUserDisplay(u)} ({u.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Toewijzen aan groep</Label>
                <Select
                  defaultValue={selectedTicket.assigned_group_id?.toString() || 'none'}
                  onValueChange={handleAssignGroup}
                  disabled={assigning}
                >
                  <SelectTrigger className="bg-background border-neon">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Geen groep</SelectItem>
                    {groups.map((group) => (
                      <SelectItem key={group.id} value={group.id.toString()}>
                        {group.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {assigning && (
                <div className="flex items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-neon-green" />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
